"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BackButton } from "@/components/ui/BackButton";
import { useState, useEffect } from "react";
import { isValidEmail } from "@/lib/utils";
import { emailSignIn, credentialsSignIn } from "@/lib/signInUtils";
import { handleError } from "@/lib/apiUtils";
import Link from "next/link";

type EmailAuthFormProps = {
  onBack?: () => void;
  callbackUrl?: string;
  returnToUrl?: string;
  title?: string;
  buttonText?: string;
  showTerms?: boolean;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
};

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({
  onBack,
  callbackUrl,
  returnToUrl,
  title = "Create an account",
  buttonText = "Get Started",
  showTerms = true,
  className = "",
  inputClassName = "",
  buttonClassName = "",
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // NEXT_PUBLIC_* vars are baked into the client bundle at build time.
  const testEmails = (process.env.NEXT_PUBLIC_TEST_USER_EMAILS ?? "")
    .split(",")
    .map((e) => e.trim())
    .filter(Boolean);
  const isTestEmail = testEmails.length > 0 && testEmails.includes(email);
  const isValid = isValidEmail(email);

  // Reset state when component mounts or callbackUrl changes
  useEffect(() => {
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setIsLoading(false);
  }, [callbackUrl]);

  const handleBackToOptions = () => {
    setEmail("");
    setPassword("");
    setShowPassword(false);
    onBack?.();
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isLoading) return;

    // Test account: reveal password field on first submit, sign in on second
    if (isTestEmail) {
      if (!showPassword) {
        setShowPassword(true);
        return;
      }
      if (!password) return;
      setIsLoading(true);
      try {
        await credentialsSignIn(email, password, callbackUrl ?? "/");
      } catch (error) {
        setIsLoading(false);
        handleError(error, { reportError: false });
      }
      return;
    }

    // Normal magic-link flow
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      await emailSignIn(email, callbackUrl, returnToUrl);
    } catch (error) {
      setIsLoading(false);
      handleError(error, { reportError: true });
    }
  };

  return (
    <div className={className}>
      {(onBack || title) && (
        <div className="relative mb-6 flex items-center justify-center">
          {onBack && (
            <BackButton
              onClick={handleBackToOptions}
              size="sm"
              className="absolute left-0"
            />
          )}
          {title && (
            <h2 className="text-center text-3xl font-semibold text-foreground">
              {title}
            </h2>
          )}
        </div>
      )}

      <form
        onSubmit={handleEmailSubmit}
        className="flex w-full flex-col items-center justify-center gap-y-5 py-5"
      >
        <div className="w-full max-w-[240px] md:max-w-[320px]">
          <Input
            className={inputClassName || undefined}
            name="emailAddress"
            placeholder="Email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.toLowerCase());
              setShowPassword(false);
              setPassword("");
            }}
            containerClassName="w-full flex-1"
            disabled={isLoading}
            required
          />
        </div>
        {showPassword && (
          <div className="w-full max-w-[240px] md:max-w-[320px]">
            <Input
              className={inputClassName || undefined}
              name="password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              containerClassName="w-full flex-1"
              disabled={isLoading}
              autoFocus
              required
            />
          </div>
        )}
        <Button
          type="submit"
          className={`w-full max-w-[240px] bg-brand-gold md:max-w-[320px] ${buttonClassName}`}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </form>

      {showTerms && (
        <div className="px-6 pt-2 text-center text-xs text-muted-foreground">
          By creating an account you are accepting Gondola&apos;s{" "}
          <Link
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </Link>
        </div>
      )}
    </div>
  );
};

export default EmailAuthForm;
